import { colors } from 'baseui/tokens';
import stylesColors from 'common/styles/styles.colors';
import { string2money } from 'common/utils/string';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import Text from 'components/elements/text';
import { format } from 'date-fns';
import { SeparatorHorizontal, SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { TotalContent, TotalItemContent } from './styles';
import SummaryAddOnsItem from './summary-add-ons-item';
import SummaryItem from './summary-item';
import useRecalculate from '../../hooks/use-recalculate';
import { CruisePurchaseFormType } from '../form-type';

interface Props {
  onClose: () => void;
}

function Total({ label, price }: { label: string; price: number }) {
  return (
    <TotalContent>
      <TotalItemContent>
        <Text variant="LabelMedium">{label}:</Text>
        <SeparatorHorizontal $margin={8} />
        <Text variant="HeadingXSmall">{`Rp ${string2money(price)}`}</Text>
      </TotalItemContent>
    </TotalContent>
  );
}

export default function SummaryDrawer(props: Props) {
  const { t } = useTranslation();
  const { control } = useFormContext<CruisePurchaseFormType>();
  const cruisePurchase = useWatch({
    control,
    name: 'cruisePurchases',
  });
  const { onClose } = props;

  const { discount, otherCost, total, subtotal } = useRecalculate();

  return (
    <>
      <DrawerHeader
        onClose={onClose}
        title={t('ticketOrders:cruise_details')}
      />
      <DrawerBody>
        <Text variant="HeadingXSmall">{`${
          cruisePurchase[0].cruiseName || '-'
        } | ${
          cruisePurchase[0].startedAt
            ? format(cruisePurchase[0].startedAt, 'dd-MM-yyyy')
            : ''
        } - ${
          cruisePurchase[0].endedAt
            ? format(cruisePurchase[0].endedAt!, 'dd-MM-yyyy')
            : ''
        } | ${cruisePurchase[0].route || '-'}`}</Text>
        <SeparatorVertical $margin={24} />
        {cruisePurchase.map((cp, idx) => {
          const cruiseGuests = cp.cruisePurchaseGuests;
          const cruiseDetails = cp.cruisePurchaseDetails;
          const cruiseAddOns = cp.cruisePurchaseAddOns;

          return (
            <React.Fragment key={idx}>
              <Text
                variant="LabelMedium"
                $style={{ color: stylesColors.primaryDefault }}
              >
                {t('cruise:cabin_detail')} {idx + 1}
              </Text>
              <SeparatorVertical $margin={16} />
              {cp.isGroupedPrice ? (
                <>
                  <SummaryItem
                    data={cruiseDetails}
                    guests={cruiseGuests}
                    isGrouped
                  />
                  {cruiseAddOns.length > 0 ? (
                    <SeparatorVertical $margin={24} />
                  ) : (
                    <SeparatorVertical $margin={24}>
                      <div
                        style={{ height: 1, backgroundColor: colors.gray200 }}
                      />
                    </SeparatorVertical>
                  )}
                </>
              ) : (
                <>
                  {cruiseDetails.map((cruiseDetail, idx) => {
                    const guestAddOns = cruiseAddOns.filter(
                      (cruiseAddOn) =>
                        cruiseAddOn.guestId === cruiseDetail.guestId,
                    );

                    return (
                      <React.Fragment key={idx}>
                        <SummaryItem
                          data={[cruiseDetail]}
                          guests={cruiseGuests}
                        />
                        {guestAddOns.length > 0 && (
                          <>
                            <SeparatorVertical $margin={12} />
                            <Text
                              variant="ParagraphSmall"
                              $style={{ color: colors.gray500 }}
                            >
                              {t('ticketSales:add_ons')}
                            </Text>
                            {guestAddOns.map((guestAddOn, idx) => (
                              <SummaryAddOnsItem key={idx} data={guestAddOn} />
                            ))}
                          </>
                        )}
                        <SeparatorVertical $margin={24}>
                          <div
                            style={{
                              height: 1,
                              backgroundColor: colors.gray200,
                            }}
                          />
                        </SeparatorVertical>
                      </React.Fragment>
                    );
                  })}
                  {/* {cruiseDetails.map((cruiseDetail, idx) => (
                    <SummaryItem key={idx} data={[cruiseDetail]} />
                  ))} */}
                </>
              )}
              {!!cruiseAddOns.length && cp.isGroupedPrice && (
                <>
                  <Text
                    variant="ParagraphSmall"
                    $style={{ color: colors.gray500 }}
                  >
                    {t('ticketSales:add_ons')}
                  </Text>
                  {cruiseAddOns.map((addOns, idx) => (
                    <React.Fragment key={idx}>
                      <SummaryAddOnsItem data={addOns} isGrouped />
                    </React.Fragment>
                  ))}
                  <SeparatorVertical $margin={24}>
                    <div
                      style={{ height: 1, backgroundColor: colors.gray200 }}
                    />
                  </SeparatorVertical>
                </>
              )}
            </React.Fragment>
          );
        })}
        <Total label={t('common:subtotal')} price={subtotal} />
        <Total label={t('common:discount')} price={discount} />
        <Total label={t('common:other_cost')} price={otherCost} />
        <Total label={t('common:total')} price={total} />
      </DrawerBody>
    </>
  );
}
